/** @jsx jsx */
import { jsx } from "frontend/src/utils/jsx"
import { SectionGrid, Grid, Flex } from "frontend/src/components/Layout"
import { Logo } from "frontend/src/components/Logo"
import MapComponent from "./MapComponent"
import { useState, useRef, useEffect } from "react"
import { MdSearch, MdClose } from "react-icons/md"
import { FaCrosshairs } from "react-icons/fa"
/* eslint-disable react-hooks/exhaustive-deps */

// preliminary key variable
const apiKey = "AIzaSyDircXqv3fMxN7Q-iHaAkZ2JIETt2NG9h0"

// helper functions
// get the approximate distance (in km) between two points on earth
function getDistance(lat_a, lng_a, lat_b, lng_b) {
  // bring angles from degrees to radians
  const [lar_a, lnr_a, lar_b, lnr_b] = [lat_a, lng_a, lat_b, lng_b].map(
    angle => (angle * Math.PI) / 180
  )
  const distance =
    6371 *
    Math.acos(
      Math.sin(lar_a) * Math.sin(lar_b) +
        Math.cos(lar_a) * Math.cos(lar_b) * Math.cos(lnr_b - lnr_a)
    )
  return distance
}

const Nbsp = () => "\u00A0"

// subcomponents
// heading of the store finder
function StoreFinderHeading({ title, shortTitle }) {
  return (
    <h2 sx={{ variant: "text.h2", textAlign: "center", marginBottom: 11 }}>
      <span sx={{ display: ["none", "inline"] }}>{title}</span>
      <span sx={{ display: ["inline", "none"] }}>{shortTitle}</span>
    </h2>
  )
}

// part of the store finder containing the map
function MapBox({
  mapWidth,
  mapBox,
  storeList,
  defaultZoom,
  coordinates: { lat, lng },
  setCoordinates,
  selectedStore,
  setSelectedStore,
  getStores,
}) {
  const unsetSelectedStore = () => {
    setSelectedStore(null)
  }
  return (
    <div
      sx={{
        height: [mapWidth, 1.3 * mapWidth, 0.75 * mapWidth],
        overflow: "hidden",
        position: "relative",
      }}
      ref={mapBox}
    >
      {lat && lng && (
        <MapComponent
          defZoom={defaultZoom}
          defCenter={[lat, lng]}
          stores={storeList}
          center={[lat, lng]}
          selectedStore={selectedStore}
          setSelectedStore={setSelectedStore}
          setCoordinates={setCoordinates}
          mapWidth={mapWidth}
          getStores={getStores}
        />
      )}
      {selectedStore && (
        <div
          sx={{
            backgroundColor: "white",
            opacity: 0.8,
            position: "absolute",
            left: "35%",
            top: "35%",
            padding: 3,
            borderWidth: "1px",
            borderStyle: "solid",
            borderColor: "bg-dark",
          }}
        >
          <div sx={{ textAlign: "right" }}>
            <button
              onClick={unsetSelectedStore}
              sx={{ background: "transparent", border: "none", outline: "none", padding: 0 }}
            >
              <MdClose style={{ height: "1em" }} />
            </button>
          </div>
          <h5>{selectedStore.storeName}</h5>
          {selectedStore.storeAddress.street && (
            <p>
              {selectedStore.storeAddress.street} {selectedStore.storeAddress.number},
              {selectedStore.storeAddress.zipCode} {selectedStore.storeAddress.city}
            </p>
          )}
        </div>
      )}
    </div>
  )
}

// part of the store finder containing the input form and the list of stores
function InfoBox({
  mapWidth,
  listTitle,
  placeholderText,
  storeList,
  coordinates: { lat, lng },
  getDirections,
  setCoordinates,
  selectedStore,
  setSelectedStore,
  getStores,
}) {
  const [postalCode, setPostalCode] = useState("")
  const inputFieldRef = useRef(null)
  // fetch geocoordinate of postal code
  function fetchCoordinates() {
    const api = process.env.GOOGLE_KEY || apiKey
    const url = `https://maps.googleapis.com/maps/api/geocode/json?address=${postalCode},+Germany&key=${api}`
    fetch(url)
      .then(response => response.json())
      .then(data => {
        if (data.status === "OK") {
          setCoordinates({
            lat: data.results[0].geometry.location.lat,
            lng: data.results[0].geometry.location.lng,
          })
          inputFieldRef.current.value = ""
          getStores(data.results[0].geometry.location.lat, data.results[0].geometry.location.lng)
        } else {
          console.log(data)
        }
      })
  }
  const fetchCoordinatesGeo = () => {
    if ("geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition(position => {
        setCoordinates({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        })
        getStores(position.coords.latitude, position.coords.longitude)
      })
    }
  }
  // add distance to store list
  const newStoreList = storeList.map(store => {
    const { storeCoordinates } = store
    const { latitude: storeLat, longitude: storeLng } = storeCoordinates
    const dist = getDistance(lat, lng, storeLat, storeLng)
    return { ...store, distance: dist }
  })
  return (
    <div
      sx={{
        width: [mapWidth, mapWidth, "unset"],
        height: [mapWidth, 1.3 * mapWidth, 0.75 * mapWidth],
        backgroundColor: "background",
        padding: 8,
      }}
    >
      <h3
        sx={{
          variant: "text.h4",
          textTransform: "uppercase",
          textAlign: "center",
          display: ["none", "block"],
        }}
      >
        {listTitle}
      </h3>
      <InputForm
        placeholderText={placeholderText}
        inputFieldRef={inputFieldRef}
        setPostalCode={setPostalCode}
        fetchCoordinates={fetchCoordinates}
        fetchCoordinatesGeo={fetchCoordinatesGeo}
        setSelectedStore={setSelectedStore}
      />
      <StoreList
        list={newStoreList}
        getDir={getDirections}
        coordinates={{ lat, lng }}
        setCoordinates={setCoordinates}
        plz={postalCode}
        selectedStore={selectedStore}
        setSelectedStore={setSelectedStore}
        mapWidth={mapWidth}
        getStores={getStores}
      />
    </div>
  )
}

// input form component
function InputForm({
  placeholderText,
  fetchCoordinates,
  fetchCoordinatesGeo,
  setPostalCode,
  inputFieldRef,
  setSelectedStore,
}) {
  const handleSubmit = event => {
    if (event.key === "Enter") {
      fetchCoordinates()
      setSelectedStore(null)
    }
  }
  return (
    <div
      sx={{
        boxShadow: "0px -0.6px 0px rgba(0, 0, 0, 0.06), 0px 0.6px 0px rgba(0, 0, 0, 0.0564084)",
        padding: 3,
      }}
    >
      <Flex
        direction="row"
        justify="flex-start"
        sx={{
          borderWidth: "0.6px",
          paddingY: 1,
          marginY: 3,
          borderStyle: "solid",
          borderColor: "blue_100",
          borderRadius: "24px",
          height: "1.4em",
        }}
      >
        <button
          sx={{ backgroundColor: "transparent", border: "none" }}
          onClick={fetchCoordinatesGeo}
        >
          <FaCrosshairs style={{ height: "1em" }} />
        </button>
        <input
          type="text"
          name="search"
          id="search"
          placeholder={placeholderText}
          sx={{ backgroundColor: "transparent", border: "none", width: ["90%", "90%", "unset"] }}
          onChange={e => setPostalCode(e.target.value)}
          onKeyPress={e => handleSubmit(e)}
          ref={inputFieldRef}
        />
        <button sx={{ backgroundColor: "transparent", border: "none" }} onClick={fetchCoordinates}>
          <MdSearch style={{ height: "1em" }} />
        </button>
      </Flex>
    </div>
  )
}

// list of stores
function StoreList({
  list,
  getDir,
  coordinates,
  setCoordinates,
  postalCode,
  selectedStore,
  setSelectedStore,
  mapWidth,
  getStores,
}) {
  return (
    <div
      sx={{
        overflow: "scroll",
        height: [
          `calc(${mapWidth}px - 1.4em - 2px)`,
          `calc(${mapWidth}px - 1.4em - 2px)`,
          `calc(${0.75 * mapWidth}px - 154px)`,
        ],
      }}
    >
      {list &&
        list.map((singleStore, index) => (
          <StoreListItem
            store={singleStore}
            getDir={getDir}
            key={index}
            coordinates={coordinates}
            setCoordinates={setCoordinates}
            postalCode={postalCode}
            selectedStore={selectedStore}
            setSelectedStore={setSelectedStore}
            getStores={getStores}
          />
        ))}
    </div>
  )
}

// single entry in the list of stores
function StoreListItem({
  store,
  getDir,
  setCoordinates,
  setZoom,
  postalCode,
  coordinates: { lat, lng },
  selectedStore,
  setSelectedStore,
  getStores,
}) {
  const {
    storeLogo,
    storeName,
    storeAddress: { street, number, zipCode, city },
    storeCoordinates: { latitude, longitude },
    distance,
  } = store
  const urlStreet = `${street.split(" ").join("+")}+${number.split(" ").join("+")}`
  const urlZip = `${zipCode}+${city}`
  const urlAddress =
    street && street !== "" ? `${urlStreet},+${urlZip}` : `${latitude},${longitude}`
  const departAddress = postalCode ? `${postalCode}` : `${lat},${lng}`
  const routePlannerUrl = `https://www.google.de/maps/dir/${departAddress}/${urlAddress}`
  const showAddress = street ? `${street} ${number}, ${zipCode} ${city}` : <Nbsp />
  let backgroundColor
  if (selectedStore) {
    if (
      latitude === selectedStore.storeCoordinates.latitude &&
      longitude === selectedStore.storeCoordinates.longitude
    ) {
      backgroundColor = "gold_100"
    } else {
      backgroundColor = "none"
    }
  } else {
    backgroundColor = "none"
  }
  return (
    <div
      role="button"
      tabIndex="0"
      sx={{
        boxShadow:
          "0px -0.7495px 0px rgba(0, 0, 0, 0.06), 0px 0.7495px 0px rgba(0, 0, 0, 0.0564084)",
        backgroundColor: `${backgroundColor}`,
        paddingY: 2,
      }}
      onClick={() => {
        setSelectedStore(store)
        setCoordinates({ lat: Number(latitude), lng: Number(longitude) })
        getStores(latitude, longitude)
      }}
      onKeyDown={ev => {
        if (ev.keyCode === 13) {
          setSelectedStore(store)
          setCoordinates({ lat: Number(latitude), lng: Number(longitude) })
          getStores(latitude, longitude)
        }
      }}
    >
      <Flex direction="row" justify="space-between">
        <Logo name={storeLogo} sx={{ maxHeight: "20px", maxWidth: ["unset", "40%"] }} />
        <a
          href={routePlannerUrl}
          target="_blank"
          rel="noopener noreferrer"
          sx={{ variant: "text.h5", color: "almostBlack" }}
        >
          {getDir}
        </a>
      </Flex>
      <Flex direction="row" justify="space-between">
        <h5 sx={{ variant: "text.h5" }}>{storeName}</h5>
        <p sx={{ variant: "text.body1" }}>{distance.toFixed(1)} km</p>
      </Flex>
      <p sx={{ variant: "text.body1" }}>{showAddress}</p>
    </div>
  )
}

// main component
function StoreFinder({
  title,
  listTitle,
  getDirections,
  defaultZoom,
  defaultCenter: { latitude, longitude },
  searchField: { placeholder },
}) {
  // references
  const mapBox = useRef(null)
  // states
  const [coordinates, setCoordinates] = useState({ lat: latitude, lng: longitude })
  const [mapWidth, setMapWidth] = useState(null)
  const [selectedStore, setSelectedStore] = useState(null)
  // stores
  const [storeList, setStoreList] = useState([])
  const [height, setHeight] = useState(null)
  const WrapperRef = useRef()
  const getStores = (lat, lng) => {
    const storeUrl = `https://apotheken.vercel.app/geo?lat=${lat}&lng=${lng}`
    fetch(storeUrl)
      .then(response => response.json())
      .then(data => data.retailerStoreList)
      .then(list => {
        const listSorted = list
          .map(store => {
            const {
              storeCoordinates: { latitude: storeLatitude, longitude: storeLongitude },
            } = store
            const distance = getDistance(lat, lng, storeLatitude, storeLongitude)
            return { ...store, distance }
          })
          .filter(store => store.distance <= 12)
          .sort((a, b) => a.distance - b.distance)
        setStoreList(listSorted)
      })
      .catch(err => console.error(err))
  }
  useEffect(() => {
    getStores(latitude, longitude)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  // adjust size of components
  useEffect(() => {
    try {
      function calcWidth() {
        if ("current" in mapBox) {
          if (typeof mapBox.current === "object") {
            if ("offsetWidth" in mapBox.current) {
              if (window && mapBox.current.offsetWidth >= window.innerWidth) {
                setMapWidth(window.innerWidth - 50)
              } else {
                setMapWidth(mapBox.current.offsetWidth)
              }
            }
          }
        }
      }
      calcWidth()
      window.addEventListener("resize", calcWidth)
    } catch (error) {
      console.error(error)
    }
  }, [setMapWidth])
  try {
    const postDimensions = () => {
      if (
        window &&
        WrapperRef.hasOwnProperty("current") &&
        WrapperRef.current.hasOwnProperty("offsetHeight") &&
        height !== WrapperRef.current.offsetHeight
      ) {
        const myHeight = Math.max(
          height,
          WrapperRef.current.offsetHeight,
          WrapperRef.current.scrollHeight
        )
        setHeight(myHeight)
        window.parent.postMessage({ frameHeight: myHeight }, "*")
        /*console.log(myHeight);*/
      }
    }
    useEffect(() => {
      postDimensions()
    }, [])
    if (window) {
      window.onload = () => postDimensions()
      window.onresize = () => postDimensions()
    }
  } catch (error) {
    console.dir(error)
  }
  // render method
  return (
    <SectionGrid Ref={WrapperRef} sx={{ backgroundColor: "blue_100", paddingY: 6 }}>
      <SectionGrid.wide sx={{ paddingX: ["none", 7] }}>
        <StoreFinderHeading title={title} shortTitle={listTitle} />
        {storeList && (
          <Grid columns={["1fr", "1fr", "1fr 1fr", "2fr 1fr"]} gap={7}>
            {coordinates.lat && coordinates.lng && (
              <MapBox
                mapWidth={mapWidth}
                mapBox={mapBox}
                storeList={storeList}
                defaultZoom={defaultZoom}
                coordinates={coordinates}
                setCoordinates={setCoordinates}
                selectedStore={selectedStore}
                setSelectedStore={setSelectedStore}
                getStores={getStores}
              />
            )}
            <InfoBox
              mapWidth={mapWidth}
              listTitle={listTitle}
              placeholderText={placeholder}
              storeList={storeList}
              coordinates={coordinates}
              setCoordinates={setCoordinates}
              getDirections={getDirections}
              selectedStore={selectedStore}
              setSelectedStore={setSelectedStore}
              getStores={getStores}
            />
          </Grid>
        )}
      </SectionGrid.wide>
    </SectionGrid>
  )
}

export { StoreFinder }
